// src/encounter/lib/to-family-history-request.ts
// Build the wire bodies for the IPS-rail Family History section. Pure — mirrors the Flags +
// Allergy + Condition mappers. LIFTS the body-assembly logic from the chart-tab editor
// (src/patients/components/chart/add-family-history-editor.tsx submit()) — it already encodes
// every Family-History wire quirk (curl-verified by Andrew 2026-05-20). REUSES the curl-verified
// CreateFamilyMedicalHistoryBody / UpdateFamilyMedicalHistoryBody contracts (patient-chart-api.ts).
//
// A Family-History record = ONE relationship (coded master row) + an ARRAY of ICD-10 diagnosis
// rows. Each row carries description + From(required)/To(optional) dates. On EDIT, existing rows
// round-trip their per-row recorder (performerDetails) + speciality + updatedby/updatedDate;
// newly-added rows get the current user stamped on `updatedby`.
//
// Actions:
//  - add    → POST /api/Patients/CreateFamilyMedicalHistory   (CreateFamilyMedicalHistoryBody).
//  - edit   → PUT  /api/Patients/familyMedicalHistory/{id}    (UpdateFamilyMedicalHistoryBody).
//  - delete → PUT  /api/Patients/familyMedicalHistory/{id}    (isDeleted:true; round-trip rest).
//
// ⚠️ WIRE QUIRKS (preserve verbatim — see CreateFamilyMedicalHistoryBody doc + PROD-842):
//   dataAbsentReason:"n/a", fullName:"n/a", outCome:"n/a" (typo, capital C — do NOT rename),
//   note:"" default, gender:null, age:0 (sentinel, NOT null), dob:null, status:"Active",
//   statusReason:null, relationship.system:"NAB041".
import type {
  CreateFamilyMedicalHistoryBody,
  FamilyHistoryCodeElement,
  PatientFamilyHistory,
  PerformerDetailsFull,
  UpdateFamilyMedicalHistoryBody,
} from '@/types/patient-chart-api';
import type { CodeWordCase } from '@/types/patient-api';
import type { ConditionCode } from '@/types/patient-chart-api';

export interface FamilyHistoryRequestContext {
  /**
   * The encounter this item is being recorded IN — stamped on CREATE as the wire `encounter`.
   *
   * FLUTTER PARITY. Its shared clinical-info popup applies `data.encounter = encounterId` to EVERY
   * summary type before dispatching per type (`popup_widget.dart:152`), and the per-tab add paths do
   * the same (`allergies_tab.dart:785`, `history_tab.dart:1318,1490` → `encounter:
   * widget.encounterId`). Nearly every patient model carries the field — allergies, immunization,
   * conditions, procedures, medications, labs, flags, medical + social history.
   *
   * PA sent a hardcoded `encounter: null` here, so an item added on the pre-encounter had no record
   * of which visit produced it. Andrew, 2026-08-28: "when i call any api in the pre-encounter for
   * allergy, vaccine, family history, social history,.... we should add encounterId value."
   *
   * Optional on purpose. These builders are ENCOUNTER-ONLY — the patients-module chart has its own
   * path (`patients/services/use-create-allergy.ts` etc.), so an earlier version of this comment
   * claiming they "also serve the chart" was wrong. The reason it stays optional is that the
   * encounter itself can lack an id: a deep-link with no `createContext`, a Booked appointment
   * (where the open-phase create is deliberately gated), or an open-phase create that failed. In
   * those the rail still renders and still writes. Absent → `null`, exactly the previous behaviour.
   */
  encounterId?: string | null;
  patientId: string;
  /** Current user's userId GUID — stamped as `createdBy` on CREATE and `updatedby` on
   *  newly-added rows during EDIT. */
  userId: string;
}

/** The relationship master selection (coded). `system` defaults to the FamilyHistory taxonomy. */
export interface FamilyHistoryRelationship {
  code: string | null;
  name: string | null;
  system: string | null;
}

/** One diagnosis row in the dialog. `code` is the flattened ICD-10 ConditionCode (run through
 *  flattenDiagnosisRow at the search boundary). `_carry` holds the original record's round-trip
 *  fields for an EXISTING row (undefined on a newly-added row). */
export interface FamilyHistoryRow {
  code: ConditionCode;
  description?: string | null;
  /** ISO 8601 no-Z (from DynamicDateInput). Required From — null only when unparseable. */
  fromTime: string | null;
  /** The user's verbatim From string (DD/MM/YYYY, MM/YYYY, YYYY, 1Y/2M/3D). */
  fromDisplay: string | null;
  /** ISO 8601 no-Z. Optional To. */
  toTime: string | null;
  /** The user's verbatim To string. Null/empty when not set. */
  toDisplay: string | null;
  /** Round-trip carry for an EXISTING row — preserved verbatim on EDIT. Absent on new rows. */
  carry?: {
    updatedby?: string | null;
    updatedDate?: string | null;
    performerDetails?: Partial<PerformerDetailsFull> | null;
    speciality?: CodeWordCase | null;
  };
}

/** The dialog's resolved selection. */
export interface FamilyHistoryFormValue {
  relationship: FamilyHistoryRelationship;
  rows: FamilyHistoryRow[];
}

export type FamilyHistoryAction =
  | { type: 'add'; value: FamilyHistoryFormValue }
  | { type: 'edit'; existing: PatientFamilyHistory; value: FamilyHistoryFormValue }
  | { type: 'delete'; existing: PatientFamilyHistory };

const RELATIONSHIP_SYSTEM = 'NAB041';

/** Normalize a performerDetails carry to the strict round-trip shape. Mirrors the chart-tab
 *  `normalizePerformerDetails` (add-allergy-dialog.tsx) used in the lifted editor. */
function normalizePerformerDetails(
  pd: Partial<PerformerDetailsFull> | null | undefined,
): PerformerDetailsFull | null {
  if (!pd) return null;
  return {
    id: pd.id ?? null,
    name: pd.name ?? null,
    photo: pd.photo ?? null,
    speciality: pd.speciality
      ? {
          specialitySkillId: pd.speciality.specialitySkillId ?? null,
          code: pd.speciality.code
            ? {
                code: pd.speciality.code.code ?? null,
                displayName: pd.speciality.code.displayName ?? null,
                system: pd.speciality.code.system ?? null,
                description: pd.speciality.code.description ?? null,
                shortName: pd.speciality.code.shortName ?? null,
              }
            : null,
        }
      : null,
    timeZone: pd.timeZone ?? null,
    organizationId: pd.organizationId ?? null,
    roleName: pd.roleName ?? null,
  };
}

function normalizeSpeciality(s: Partial<CodeWordCase> | null | undefined): CodeWordCase | null {
  if (!s) return null;
  return {
    code: s.code ?? null,
    display: s.display ?? null,
    system: s.system ?? null,
    description: s.description ?? null,
    shortName: s.shortName ?? null,
  };
}

/** The relationship Coding shipped on the wire (CodeWordCase; system defaults to NAB041). */
function relationshipCoding(rel: FamilyHistoryRelationship): CodeWordCase {
  return {
    code: rel.code ?? null,
    display: rel.name ?? null,
    system: rel.system ?? RELATIONSHIP_SYSTEM,
    description: null,
    shortName: null,
  };
}

/**
 * Map one form row → a FamilyHistoryCodeElement. Lifted verbatim from the chart-tab editor's
 * submit() row mapping. Existing rows (carry present) round-trip their original performerDetails +
 * speciality + updatedby/updatedDate; newly-added rows ship null per-row recorder and the current
 * user's id on `updatedby` (server fills the per-row recorder on insert).
 */
function toCodeElement(row: FamilyHistoryRow, userId: string): FamilyHistoryCodeElement {
  const carry = row.carry;
  const isExistingRow = !!carry && (!!carry.updatedby || !!carry.performerDetails);
  const description = row.description?.trim() || null;
  const toDisplay = row.toDisplay?.trim() ? row.toDisplay : null;
  return {
    system: row.code.system ?? null,
    code: row.code.code ?? null,
    display: row.code.display ?? null,
    description,
    fromTime: row.fromTime,
    fromDisplayTime: row.fromDisplay,
    toTime: row.toTime ?? null,
    toDisplayTime: toDisplay,
    isDeleted: false,
    // Existing rows preserve their original updatedby (audit trail); newly added rows on an Edit
    // get stamped with the editing user. Add-mode rows also stamp the current user.
    updatedby: isExistingRow ? (carry?.updatedby ?? userId) : userId,
    updatedDate: carry?.updatedDate ?? null,
    performerDetails: normalizePerformerDetails(carry?.performerDetails),
    speciality: normalizeSpeciality(carry?.speciality),
  };
}

/** CREATE body. createdBy = current user; createdDate null (server fills). `encounter` is now the
 *  CALLER'S — it is not server-filled: Flutter stamps it client-side on every clinical write
 *  (`popup_widget.dart:152`), and this comment's "server fills" claim was the assumption behind the
 *  hardcoded null that lost the visit link. */
export function buildCreateFamilyHistoryBody(
  value: FamilyHistoryFormValue,
  ctx: FamilyHistoryRequestContext,
): CreateFamilyMedicalHistoryBody {
  return {
    identifier: null,
    code: value.rows.map((r) => toCodeElement(r, ctx.userId)),
    status: 'Active',
    patient: ctx.patientId,
    dataAbsentReason: 'n/a',
    fullName: 'n/a',
    relationship: relationshipCoding(value.relationship),
    gender: null,
    age: 0,
    dob: null,
    outCome: 'n/a',
    note: '',
    statusReason: null,
    encounter: ctx.encounterId ?? null,
    isDeleted: false,
    createdDate: null,
    createdBy: ctx.userId,
  };
}

/** Shared EDIT/DELETE body — round-trips the original record (createdBy/createdDate/encounter +
 *  note). On EDIT the code[] is rebuilt from the form rows (existing rows carry their per-row
 *  recorder); on DELETE the form is absent and the record's existing rows round-trip verbatim. */
function buildUpdateFamilyHistoryBody(
  existing: PatientFamilyHistory,
  value: FamilyHistoryFormValue | undefined,
  isDeleted: boolean,
  ctx: FamilyHistoryRequestContext,
): UpdateFamilyMedicalHistoryBody {
  const code: FamilyHistoryCodeElement[] = value
    ? value.rows.map((r) => toCodeElement(r, ctx.userId))
    : // DELETE: round-trip the record's existing rows verbatim (filter tombstones).
      (existing.code ?? [])
        .filter((c) => c.isDeleted !== true)
        .map<FamilyHistoryCodeElement>((c) => ({
          system: c.system ?? null,
          code: c.code ?? null,
          display: c.display ?? null,
          description: c.description ?? null,
          fromTime: c.fromTime ?? null,
          fromDisplayTime: c.fromDisplayTime ?? null,
          toTime: c.toTime ?? null,
          toDisplayTime: c.toDisplayTime ?? null,
          isDeleted: false,
          updatedby: c.updatedby ?? null,
          updatedDate: c.updatedDate ?? null,
          performerDetails: normalizePerformerDetails(c.performerDetails),
          speciality: normalizeSpeciality(c.speciality),
        }));
  return {
    identifier: existing.identifier!,
    code,
    status: 'Active',
    patient: ctx.patientId,
    dataAbsentReason: 'n/a',
    fullName: 'n/a',
    relationship: value
      ? relationshipCoding(value.relationship)
      : normalizeSpeciality(existing.relationship as Partial<CodeWordCase> | null),
    gender: null,
    age: 0,
    dob: null,
    outCome: 'n/a',
    note: existing.note ?? '',
    statusReason: null,
    /**
     * The CURRENT encounter wins, falling back to the record's own.
     *
     * FLUTTER PARITY (Andrew, 2026-08-28: "we will do as flutter"). Its shared popup assigns
     * `data.encounter = encounterId` BEFORE branching on `identifier != null`
     * (`popup_widget.dart:152`), so an edit re-stamps the item with the encounter it is being
     * edited IN — not the one that first recorded it. An earlier pass here preserved
     * `existing.encounter` on the reasoning that editing an old allergy shouldn't re-attribute it;
     * that was my call, and Andrew overruled it. It also fixes items PA wrote before the create-path
     * fix: they carry `encounter: null` and the next edit now heals them, where round-tripping would
     * have kept them null forever.
     *
     * THE `?? existing.encounter` IS NOT DECORATION. Flutter can assign unconditionally because its
     * popup guarantees an id first (`if (isBlank(encounterId)) createEncounter()`, initState). We
     * have no such guarantee: a deep-link with no `createContext`, a Booked appointment (open-phase
     * create gated), or a failed open-phase create all leave `encounterId` null while the rail still
     * renders and still writes. Assigning unconditionally in those cases would write null over a
     * real link and DESTROY the record's provenance. So: re-stamp when we have an encounter,
     * preserve when we don't.
     *
     * (An earlier version of this note justified the fallback with the patients-module chart. That
     * was wrong — the chart has its own request path and never calls these builders.)
     */
    encounter: ctx.encounterId ?? existing.encounter ?? null,
    isDeleted,
    createdDate: existing.createdDate ?? null,
    createdBy: existing.createdBy ?? null,
  };
}

export interface FamilyHistoryRequest {
  kind: 'create' | 'update';
  /** Family-history GUID for the update path (`PUT /familyMedicalHistory/{id}`); undefined on
   *  create. */
  familyHistoryId?: string;
  body: CreateFamilyMedicalHistoryBody | UpdateFamilyMedicalHistoryBody;
}

/**
 * Resolve a family-history action into endpoint + body.
 *  - add    → CREATE. Null when no relationship picked or no problem rows.
 *  - edit   → UPDATE. Null without an identifier, no relationship, or empty rows.
 *  - delete → UPDATE soft-delete. Null without an identifier.
 */
export function buildFamilyHistoryRequest(
  action: FamilyHistoryAction,
  ctx: FamilyHistoryRequestContext,
): FamilyHistoryRequest | null {
  if (action.type === 'add') {
    if (!action.value.relationship.code && !action.value.relationship.name) return null;
    if (action.value.rows.length === 0) return null;
    return { kind: 'create', body: buildCreateFamilyHistoryBody(action.value, ctx) };
  }

  if (!action.existing.identifier) return null;

  if (action.type === 'delete') {
    return {
      kind: 'update',
      familyHistoryId: action.existing.identifier,
      body: buildUpdateFamilyHistoryBody(action.existing, undefined, true, ctx),
    };
  }

  // edit
  if (!action.value.relationship.code && !action.value.relationship.name) return null;
  if (action.value.rows.length === 0) return null;
  return {
    kind: 'update',
    familyHistoryId: action.existing.identifier,
    body: buildUpdateFamilyHistoryBody(action.existing, action.value, false, ctx),
  };
}
